import React from 'react';
import Select from 'react-select';
import '../assets/FleetAnalytics.css';
import { LoadingSkeleton } from '../reports/LoadingSkeleton';
import deviceDataService from '../../services/deviceDataService';
import usePagination from '../utils/usePagination';
import { selectStylesMulti } from '../utils/selectStyles';

const ALL = 'ALL';
const GRID_ROWS_PER_PAGE = 5;
const EXPANDED_ROWS_PER_PAGE = 10;

const SUBSYSTEM_OPTIONS = [
    { value: ALL, label: 'All' },
    { value: 'HLOS', label: 'HLOS' },
    { value: 'MODEM', label: 'Modem' }
];

function formatValue(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '—';
}

function compareRows(a, b, column) {
    if (column === 'systemId') {
        return String(a.systemId || '').localeCompare(String(b.systemId || ''));
    }

    const aValue = a[column];
    const bValue = b[column];
    const aMissing = aValue === null || aValue === undefined || Number.isNaN(Number(aValue));
    const bMissing = bValue === null || bValue === undefined || Number.isNaN(Number(bValue));
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    return Number(aValue) - Number(bValue);
}

const MEMORY_HEALTH_SELECT_STYLES = {
    ...selectStylesMulti,
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        height: 'auto',
        borderColor: state.isFocused ? '#3253DC' : '#cbd5e1',
        boxShadow: state.isFocused ? '0 0 0 1px #3253DC' : 'none'
    }),
    valueContainer: (base) => ({
        ...base,
        minHeight: '36px',
        height: 'auto',
        maxHeight: '70px',
        overflowY: 'auto',
        flexWrap: 'wrap'
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '4px'
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#1e40af',
        fontSize: '11px'
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#3253DC',
        ':hover': { backgroundColor: '#dbeafe', color: '#1e3a8a' }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 1200 })
};

function sortIndicator(column, activeColumn, direction) {
    if (column !== activeColumn) return '↕';
    return direction === 'asc' ? '↑' : '↓';
}

function SortableHeader({ column, label, activeColumn, direction, onSort }) {
    return (
        <th scope="col">
            <button type="button" className="memory-health-sort-button" onClick={() => onSort(column)}>
                <span>{label}</span>
                <span aria-hidden="true">{sortIndicator(column, activeColumn, direction)}</span>
            </button>
        </th>
    );
}

function ExpandIcon() {
    return (
        <svg className="memory-health-expand-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3H3v5M3 3l6 6M16 3h5v5M21 3l-6 6M8 21H3v-5M3 21l6-6M16 21h5v-5M21 21l-6-6" />
        </svg>
    );
}

function MemoryStatisticsTable({ subsystem, rows, onDeviceClick, refreshing, rowsPerPage, onExpand, expanded = false }) {
    const [sortColumn, setSortColumn] = React.useState('systemId');
    const [sortDirection, setSortDirection] = React.useState('asc');
    const [currentPage, setCurrentPage] = React.useState(1);

    const sortedRows = React.useMemo(() => [...rows].sort((a, b) => {
        const result = compareRows(a, b, sortColumn);
        return sortDirection === 'asc' ? result : -result;
    }), [rows, sortColumn, sortDirection]);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [rows, sortColumn, sortDirection, rowsPerPage]);

    const { pageItems, totalPages, renderPageNumbers } = usePagination(
        sortedRows,
        currentPage,
        setCurrentPage,
        rowsPerPage
    );

    const handleSort = (column) => {
        if (column === sortColumn) {
            setSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const label = subsystem === 'HLOS' ? 'HLOS' : subsystem === 'MODEM' ? 'Modem' : 'Unknown';

    return (
        <div className={`memory-health-table-shell${expanded ? ' memory-health-table-shell-expanded' : ''}`}>
            <div className="memory-health-table-heading">
                <strong>{label} — Used Memory (MB)</strong>
                <div className="memory-health-table-actions">
                    {refreshing && (
                        <span className="memory-health-refreshing" role="status">
                            <span className="memory-health-spinner" aria-hidden="true" />
                            Updating…
                        </span>
                    )}
                    {!expanded && (
                        <button type="button" className="memory-health-expand-button" onClick={onExpand} aria-label={`Expand ${label} memory table`} title="Expand table">
                            <ExpandIcon />
                        </button>
                    )}
                </div>
            </div>
            <div className="memory-health-table-scroll">
                <table className="memory-health-table">
                    <thead>
                        <tr>
                            <SortableHeader column="systemId" label="System ID" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="sampleCount" label="Samples" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="min" label="Min (MB)" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="max" label="Max (MB)" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="average" label="Avg (MB)" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="totalCapacityMb" label="Total (MB)" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="averageUsedPercent" label="Avg Used (%)" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                            <SortableHeader column="standardDeviation" label="Std Dev (MB)" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.length === 0 ? (
                            <tr><td colSpan="8" className="memory-health-no-rows">No device rows available.</td></tr>
                        ) : pageItems.map((row) => (
                            <tr key={`${row.systemId}-${row.subSystem}`}>
                                <td>
                                    <button type="button" className="memory-health-device-link" title="Open device overview" onClick={() => onDeviceClick && onDeviceClick(row.systemId)}>
                                        {row.systemId}
                                    </button>
                                </td>
                                <td>{row.sampleCount ?? 0}</td>
                                <td>{formatValue(row.min)}</td>
                                <td>{formatValue(row.max)}</td>
                                <td>{formatValue(row.average)}</td>
                                <td>{formatValue(row.totalCapacityMb)}</td>
                                <td>{formatValue(row.averageUsedPercent)}</td>
                                <td>{formatValue(row.standardDeviation)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="memory-health-pagination">
                <span>{sortedRows.length} device{sortedRows.length === 1 ? '' : 's'} · {totalPages > 1 ? `Page ${currentPage} of ${totalPages}` : 'Page 1'}</span>
                <div className="memory-health-page-buttons">{renderPageNumbers()}</div>
            </div>
        </div>
    );
}

function applyMultiSelectChange(options, actionMeta) {
    const values = (options || []).map((option) => option.value);
    if (actionMeta.action === 'clear' || values.length === 0) return [ALL];
    if (actionMeta.action === 'select-option' && actionMeta.option?.value === ALL) return [ALL];
    const withoutAll = values.filter((value) => value !== ALL);
    return withoutAll.length ? withoutAll : [ALL];
}

function errorMessage(error) {
    if (error?.name === 'AbortError' || error?.name === 'TimeoutError') return 'The Memory Health request timed out.';
    return error?.message || 'Unable to load Memory Health statistics.';
}

function MemoryHealthSection({ timeRange, deviceTag, onDeviceClick }) {
    const isDeviceTagView = deviceTag !== undefined;
    const [selectedSubsystems, setSelectedSubsystems] = React.useState([ALL]);
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [reloadToken, setReloadToken] = React.useState(0);
    const [expandedSubsystem, setExpandedSubsystem] = React.useState(null);

    React.useEffect(() => {
        if (isDeviceTagView && !deviceTag) {
            setData(null);
            setError(null);
            setLoading(false);
            return undefined;
        }

        let active = true;
        setLoading(true);
        setError(null);
        deviceDataService.getMemoryHealth(timeRange, deviceTag ?? null, selectedSubsystems)
            .then((result) => { if (active) setData(result); })
            .catch((requestError) => { if (active) setError(requestError); })
            .finally(() => { if (active) setLoading(false); });

        return () => { active = false; };
    }, [timeRange, deviceTag, isDeviceTagView, selectedSubsystems, reloadToken]);

    React.useEffect(() => {
        if (!expandedSubsystem) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setExpandedSubsystem(null);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [expandedSubsystem]);

    const visibleSubsystems = selectedSubsystems.includes(ALL)
        ? SUBSYSTEM_OPTIONS.filter((option) => option.value !== ALL).map((option) => option.value)
        : selectedSubsystems;
    const rows = data?.rows || [];

    return (
        <div className="memory-health-section">
            <div className="memory-health-toolbar">
                <div className="memory-health-filters">
                    <label>
                        <span>Subsystem <em>{selectedSubsystems.includes(ALL) ? 'All subsystems' : `${selectedSubsystems.length} subsystems selected`}</em></span>
                        <Select
                            className="memory-health-select"
                            classNamePrefix="memory-health-select"
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isSearchable={false}
                            isLoading={loading}
                            menuPortalTarget={document.body}
                            options={SUBSYSTEM_OPTIONS}
                            value={SUBSYSTEM_OPTIONS.filter((option) => selectedSubsystems.includes(option.value))}
                            onChange={(options, actionMeta) => setSelectedSubsystems(applyMultiSelectChange(options, actionMeta))}
                            styles={MEMORY_HEALTH_SELECT_STYLES}
                            placeholder="Select subsystems…"
                            aria-label="Select memory subsystems"
                        />
                    </label>
                </div>
            </div>

            {isDeviceTagView && !deviceTag ? (
                <div className="memory-health-state">Select a device group to load Memory Health.</div>
            ) : loading ? (
                <div className="memory-health-grid"><LoadingSkeleton height="220px" /><LoadingSkeleton height="220px" /></div>
            ) : error && !data ? (
                <div className="memory-health-state memory-health-error" role="alert">
                    <strong>Memory Health unavailable.</strong>
                    <span>{errorMessage(error)}</span>
                    <button type="button" onClick={() => setReloadToken((current) => current + 1)}>Retry</button>
                </div>
            ) : (
                <>
                    {error && (
                        <div className="memory-health-partial" role="alert">
                            <span>Showing the last available Memory Health results. {errorMessage(error)}</span>
                            <button type="button" onClick={() => setReloadToken((current) => current + 1)}>Retry</button>
                        </div>
                    )}
                    <div className="memory-health-grid">
                        {visibleSubsystems.map((subsystem) => (
                            <MemoryStatisticsTable
                                key={subsystem}
                                subsystem={subsystem}
                                rows={rows.filter((row) => String(row.subSystem || '').toUpperCase() === subsystem)}
                                rowsPerPage={GRID_ROWS_PER_PAGE}
                                refreshing={loading}
                                onDeviceClick={onDeviceClick}
                                onExpand={() => setExpandedSubsystem(subsystem)}
                            />
                        ))}
                    </div>
                    {!loading && rows.length === 0 && !error && (
                        <div className="memory-health-state">No Memory Health statistics are available for this scope and time range.</div>
                    )}
                </>
            )}

            {expandedSubsystem && (
                <div className="memory-health-modal-overlay" role="presentation" onMouseDown={(event) => {
                    if (event.target === event.currentTarget) setExpandedSubsystem(null);
                }}>
                    <div className="memory-health-modal" role="dialog" aria-modal="true" aria-label={`${expandedSubsystem} memory table`}>
                        <div className="memory-health-modal-header">
                            <strong>Memory Health details</strong>
                            <button type="button" onClick={() => setExpandedSubsystem(null)} aria-label="Close expanded table">×</button>
                        </div>
                        <div className="memory-health-modal-body">
                            <MemoryStatisticsTable
                                subsystem={expandedSubsystem}
                                rows={rows.filter((row) => String(row.subSystem || '').toUpperCase() === expandedSubsystem)}
                                rowsPerPage={EXPANDED_ROWS_PER_PAGE}
                                refreshing={loading}
                                onDeviceClick={onDeviceClick}
                                expanded
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MemoryHealthSection;
